import Phaser from 'phaser'

const FONT_STACK = '"PingFang SC", "Microsoft YaHei", system-ui, sans-serif'

type PaintTool = 'cream' | 'icing-pink' | 'icing-choco'
type ToppingKind = 'candle' | 'starfruit' | 'strawberry' | 'blueberry' | 'chocolate'

interface PaintDef {
  id: PaintTool
  label: string
  color: number
}
interface ToppingDef {
  id: ToppingKind
  label: string
}

const PAINTS: PaintDef[] = [
  { id: 'cream', label: '奶油喷枪', color: 0xfffaf0 },
  { id: 'icing-pink', label: '粉色糖霜', color: 0xff7aa8 },
  { id: 'icing-choco', label: '巧克力酱', color: 0x5a3420 },
]

const TOPPINGS: ToppingDef[] = [
  { id: 'candle', label: '蜡烛' },
  { id: 'starfruit', label: '杨桃切片' },
  { id: 'strawberry', label: '草莓' },
  { id: 'blueberry', label: '蓝莓' },
  { id: 'chocolate', label: '巧克力' },
]

const CAKE_CX = 560
const CAKE_CY = 320
const CAKE_RX = 190
const CAKE_RY = 130
const CAKE_SIDE_H = 80

export default class MainScene extends Phaser.Scene {
  private paintTool: PaintTool | null = 'cream'
  private paintRT!: Phaser.GameObjects.RenderTexture
  private creamStamp!: Phaser.GameObjects.Graphics
  private icingStampPink!: Phaser.GameObjects.Graphics
  private icingStampChoco!: Phaser.GameObjects.Graphics
  private toppingLayer!: Phaser.GameObjects.Container
  private candleLayer!: Phaser.GameObjects.Container
  private paintButtons: Partial<Record<PaintTool, Phaser.GameObjects.Container>> = {}
  private toppingButtons: Partial<Record<ToppingKind, Phaser.GameObjects.Container>> = {}
  private follower!: Phaser.GameObjects.Container
  private paletteDrag: { kind: ToppingKind; ghost: Phaser.GameObjects.Container } | null = null
  private statusText!: Phaser.GameObjects.Text
  private statusTimer?: Phaser.Time.TimerEvent
  private painting = false
  private draggingObject = false
  private lastPaint?: { x: number; y: number }
  private candles: Phaser.GameObjects.Container[] = []
  private toppings: Phaser.GameObjects.Container[] = []
  private candlesLit = false

  constructor() {
    super('MainScene')
  }

  create() {
    this.drawBackground()
    this.drawTitle()
    this.drawCakeBody()

    this.paintRT = this.add
      .renderTexture(0, 0, this.scale.width, this.scale.height)
      .setOrigin(0, 0)
    const maskG = this.make.graphics({ x: 0, y: 0 }, false)
    maskG.fillStyle(0xffffff, 1)
    maskG.fillEllipse(CAKE_CX, CAKE_CY, CAKE_RX * 2, CAKE_RY * 2)
    maskG.fillRect(CAKE_CX - CAKE_RX, CAKE_CY, CAKE_RX * 2, CAKE_SIDE_H)
    maskG.fillEllipse(CAKE_CX, CAKE_CY + CAKE_SIDE_H, CAKE_RX * 2, CAKE_RY * 2)
    this.paintRT.setMask(maskG.createGeometryMask())

    this.toppingLayer = this.add.container(0, 0)
    this.candleLayer = this.add.container(0, 0)

    this.creamStamp = this.makeCreamStamp()
    this.icingStampPink = this.makeIcingStamp(0xff7aa8, 0xd6447a)
    this.icingStampChoco = this.makeIcingStamp(0x5a3420, 0x3a2010)

    this.createPaintBar()
    this.createToppingPalette()
    this.createActionButtons()

    this.follower = this.add.container(0, 0)
    this.follower.setDepth(1000)
    this.follower.setVisible(false)
    this.rebuildFollower()

    this.statusText = this.add
      .text(
        this.scale.width / 2,
        this.scale.height - 24,
        '顶部选涂抹工具→在蛋糕上点击或拖动；左侧辅料→按住拖到蛋糕上放下',
        { fontFamily: FONT_STACK, fontSize: '18px', color: '#8a6a4a' },
      )
      .setOrigin(0.5)

    this.highlightActivePaint()

    this.input.on('pointerdown', this.onPointerDown, this)
    this.input.on('pointermove', this.onPointerMove, this)
    this.input.on('pointerup', this.onPointerUp, this)
    this.input.on('pointerupoutside', this.onPointerUp, this)

    this.input.on('dragstart', () => {
      this.draggingObject = true
      this.painting = false
      this.lastPaint = undefined
    })
    this.input.on(
      'drag',
      (_p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Container, x: number, y: number) => {
        obj.setPosition(x, y)
      },
    )
    this.input.on('dragend', () => {
      this.draggingObject = false
    })
  }

  // ---------- Background & Title ----------

  private drawBackground() {
    const W = this.scale.width
    const H = this.scale.height
    const bg = this.add.graphics()
    bg.fillGradientStyle(0xfff2d6, 0xfff2d6, 0xffd8a8, 0xffd8a8, 1)
    bg.fillRect(0, 0, W, H)

    const deco = this.add.graphics()
    deco.fillStyle(0xffffff, 0.2)
    deco.fillCircle(100, 540, 40)
    deco.fillCircle(W - 70, 560, 30)
    deco.fillCircle(W - 130, 600, 22)

    const table = this.add.graphics()
    table.fillStyle(0xc68a5a, 1)
    table.fillEllipse(CAKE_CX, CAKE_CY + CAKE_SIDE_H + 70, CAKE_RX * 2 + 80, 44)
    table.fillStyle(0xa06a3a, 0.6)
    table.fillEllipse(CAKE_CX, CAKE_CY + CAKE_SIDE_H + 78, CAKE_RX * 2 + 80, 16)
  }

  private drawTitle() {
    this.add
      .text(this.scale.width / 2, 20, '做蛋糕', {
        fontFamily: FONT_STACK,
        fontSize: '26px',
        color: '#6b3a1d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0)
  }

  // ---------- Cake body ----------

  private drawCakeBody() {
    const g = this.add.graphics()
    // side band
    g.fillStyle(0xc9854a, 1)
    g.fillRect(CAKE_CX - CAKE_RX, CAKE_CY, CAKE_RX * 2, CAKE_SIDE_H)
    for (let i = 0; i < CAKE_RX * 2; i += 16) {
      g.fillStyle(0xb07238, 0.18)
      g.fillRect(CAKE_CX - CAKE_RX + i, CAKE_CY, 8, CAKE_SIDE_H)
    }
    // bottom rim
    g.fillStyle(0xb07238, 1)
    g.fillEllipse(CAKE_CX, CAKE_CY + CAKE_SIDE_H, CAKE_RX * 2, CAKE_RY * 2)
    // jam layer
    g.fillStyle(0xe85a74, 1)
    g.fillRect(CAKE_CX - CAKE_RX, CAKE_CY + CAKE_SIDE_H * 0.45, CAKE_RX * 2, 8)
    // top sponge
    g.fillStyle(0xf2b27a, 1)
    g.fillEllipse(CAKE_CX, CAKE_CY, CAKE_RX * 2, CAKE_RY * 2)
    // top highlight
    g.fillStyle(0xffd9a8, 0.55)
    g.fillEllipse(CAKE_CX - 30, CAKE_CY - 20, CAKE_RX * 1.1, CAKE_RY * 0.55)
    g.fillStyle(0xffffff, 0.18)
    g.fillEllipse(CAKE_CX + 40, CAKE_CY - 40, CAKE_RX * 0.5, CAKE_RY * 0.25)
    g.lineStyle(2, 0x8a4a20, 0.4)
    g.strokeEllipse(CAKE_CX, CAKE_CY, CAKE_RX * 2, CAKE_RY * 2)
  }

  // ---------- Stamps ----------

  private makeCreamStamp() {
    const g = this.make.graphics({ x: 0, y: 0 }, false)
    for (let i = 6; i >= 0; i--) {
      const alpha = 0.22 + (6 - i) * 0.07
      g.fillStyle(0xfffaf0, alpha)
      g.fillCircle(0, 0, 22 - i * 2.5)
    }
    g.fillStyle(0xffffff, 0.9)
    g.fillCircle(-4, -4, 5)
    return g
  }

  private makeIcingStamp(main: number, shadow: number) {
    const g = this.make.graphics({ x: 0, y: 0 }, false)
    g.fillStyle(shadow, 0.8)
    g.fillCircle(0.5, 0.5, 6)
    g.fillStyle(main, 1)
    g.fillCircle(0, 0, 5.5)
    g.fillStyle(0xffffff, 0.35)
    g.fillCircle(-1.5, -1.5, 1.8)
    return g
  }

  // ---------- Paint tool bar (top) ----------

  private createPaintBar() {
    const y = 72
    const btnW = 138
    const gap = 16
    const totalW = PAINTS.length * btnW + (PAINTS.length - 1) * gap
    let x = this.scale.width / 2 - totalW / 2 + btnW / 2
    PAINTS.forEach((p) => {
      this.paintButtons[p.id] = this.makePaintButton(p, x, y)
      x += btnW + gap
    })
  }

  private makePaintButton(def: PaintDef, x: number, y: number) {
    const c = this.add.container(x, y)
    const bg = this.add.graphics()
    const icon = this.add.graphics()
    this.drawPaintIcon(icon, def.id, -48, 0)
    const label = this.add
      .text(10, 0, def.label, {
        fontFamily: FONT_STACK,
        fontSize: '14px',
        color: '#5a3a20',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
    c.add([bg, icon, label])
    c.setSize(138, 46)
    c.setInteractive({ useHandCursor: true })
    c.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.()
      this.paintTool = def.id
      this.highlightActivePaint()
      this.rebuildFollower()
      this.showStatus(`已选：${def.label}，在蛋糕上点击或拖动`)
    })
    ;(c as any).__bg = bg
    return c
  }

  private drawPaintIcon(g: Phaser.GameObjects.Graphics, id: PaintTool, x: number, y: number) {
    g.clear()
    if (id === 'cream') {
      // sprayer bottle
      g.fillStyle(0xffffff, 1)
      g.fillRoundedRect(x - 8, y - 10, 16, 20, 3)
      g.lineStyle(1.5, 0xb08858, 1)
      g.strokeRoundedRect(x - 8, y - 10, 16, 20, 3)
      // nozzle
      g.fillStyle(0xcf9a5c, 1)
      g.fillRect(x + 6, y - 12, 6, 4)
      // puff
      g.fillStyle(0xffffff, 1)
      g.fillCircle(x + 16, y - 8, 4)
      g.fillCircle(x + 19, y - 4, 3)
    } else {
      const color = id === 'icing-pink' ? 0xff7aa8 : 0x5a3420
      // piping bag
      g.fillStyle(color, 1)
      g.fillTriangle(x - 10, y - 10, x + 10, y - 10, x + 2, y + 10)
      g.fillStyle(0xffffff, 0.35)
      g.fillTriangle(x - 8, y - 8, x - 2, y - 8, x - 4, y + 2)
      // tip
      g.fillStyle(0xaaaaaa, 1)
      g.fillRect(x, y + 8, 4, 4)
    }
  }

  private highlightActivePaint() {
    PAINTS.forEach((p) => {
      const btn = this.paintButtons[p.id]
      if (!btn) return
      const bg = (btn as any).__bg as Phaser.GameObjects.Graphics
      bg.clear()
      const active = this.paintTool === p.id
      bg.fillStyle(active ? 0xffe2b5 : 0xffffff, active ? 1 : 0.92)
      bg.fillRoundedRect(-69, -23, 138, 46, 14)
      bg.lineStyle(active ? 3 : 2, active ? 0xd46a1a : 0xcf9a5c, 1)
      bg.strokeRoundedRect(-69, -23, 138, 46, 14)
    })
  }

  // ---------- Topping palette (left) ----------

  private createToppingPalette() {
    const x = 72
    let y = 150
    // Palette title
    this.add
      .text(x, y - 34, '辅料（拖到蛋糕上）', {
        fontFamily: FONT_STACK,
        fontSize: '13px',
        color: '#6b3a1d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
    TOPPINGS.forEach((t) => {
      this.toppingButtons[t.id] = this.makeToppingButton(t, x, y)
      y += 60
    })
  }

  private makeToppingButton(def: ToppingDef, x: number, y: number) {
    const c = this.add.container(x, y)
    const bg = this.add.graphics()
    bg.fillStyle(0xffffff, 0.92)
    bg.fillRoundedRect(-60, -24, 120, 48, 14)
    bg.lineStyle(2, 0xcf9a5c, 1)
    bg.strokeRoundedRect(-60, -24, 120, 48, 14)
    const icon = this.add.graphics()
    this.drawToppingIcon(icon, def.id, -38, 0)
    const label = this.add
      .text(4, 0, def.label, {
        fontFamily: FONT_STACK,
        fontSize: '13px',
        color: '#5a3a20',
      })
      .setOrigin(0, 0.5)
    c.add([bg, icon, label])
    c.setSize(120, 48)
    c.setInteractive({ useHandCursor: true })
    c.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.()
      this.startPaletteDrag(def.id, p.x, p.y)
    })
    return c
  }

  private drawToppingIcon(g: Phaser.GameObjects.Graphics, kind: ToppingKind, x: number, y: number) {
    g.clear()
    switch (kind) {
      case 'candle':
        g.fillStyle(0xff5577, 1)
        g.fillRect(x - 3, y - 8, 6, 14)
        g.fillStyle(0xffffff, 0.5)
        g.fillRect(x - 3, y - 4, 6, 2)
        g.fillStyle(0xffd84a, 1)
        g.fillCircle(x, y - 10, 2.5)
        break
      case 'starfruit':
        this.drawStarfruit(g, x, y, 10)
        break
      case 'strawberry':
        this.drawStrawberry(g, x, y, 10)
        break
      case 'blueberry':
        this.drawBlueberry(g, x, y, 8)
        break
      case 'chocolate':
        this.drawChocolateChunk(g, x, y, 11)
        break
    }
  }

  // ---------- Follower (paint tool at pointer) ----------

  private rebuildFollower() {
    this.follower.removeAll(true)
    if (!this.paintTool) {
      this.follower.setVisible(false)
      return
    }
    const g = this.add.graphics()
    // The "tip" of the tool sits at (0,0) so the paint lands at the pointer.
    if (this.paintTool === 'cream') {
      // bottle body
      g.fillStyle(0xfdfdfd, 1)
      g.fillRoundedRect(-34, -30, 22, 34, 5)
      g.lineStyle(2, 0x8a6a4a, 1)
      g.strokeRoundedRect(-34, -30, 22, 34, 5)
      g.fillStyle(0xcf9a5c, 1)
      g.fillRect(-14, -28, 8, 6)
      // nozzle
      g.fillStyle(0x8a6a4a, 1)
      g.fillTriangle(-12, -20, -12, -12, -2, -16)
      // handle/trigger
      g.fillStyle(0xcf9a5c, 1)
      g.fillRect(-32, -4, 6, 10)
      // spray puff toward tip
      g.fillStyle(0xffffff, 0.8)
      g.fillCircle(-2, -12, 4)
      g.fillStyle(0xffffff, 0.55)
      g.fillCircle(3, -8, 3)
      g.fillStyle(0xffffff, 0.4)
      g.fillCircle(2, -4, 2.5)
    } else {
      const color = this.paintTool === 'icing-pink' ? 0xff7aa8 : 0x5a3420
      const shadow = this.paintTool === 'icing-pink' ? 0xc85680 : 0x3a2010
      // piping bag (cone) with tip at (0,0)
      g.fillStyle(shadow, 1)
      g.fillTriangle(-28, -40, 0, 0, 4, -36)
      g.fillStyle(color, 1)
      g.fillTriangle(-26, -38, -2, -2, 2, -34)
      // tied top
      g.fillStyle(0xffe0b5, 1)
      g.fillRoundedRect(-28, -46, 32, 8, 3)
      g.lineStyle(1.5, 0x8a6a4a, 1)
      g.strokeRoundedRect(-28, -46, 32, 8, 3)
      // star tip (metal)
      g.fillStyle(0xaaaaaa, 1)
      g.fillCircle(0, -1, 3)
      g.fillStyle(color, 1)
      g.fillCircle(0, 1, 2)
    }
    this.follower.add(g)
  }

  // ---------- Palette drag (toppings) ----------

  private startPaletteDrag(kind: ToppingKind, x: number, y: number) {
    if (this.paletteDrag) return
    // Picking up a topping cancels any active paint tool.
    this.paintTool = null
    this.highlightActivePaint()
    const ghost = this.add.container(x, y)
    ghost.setDepth(1200)
    ghost.setAlpha(0.92)
    const g = this.add.graphics()
    this.drawToppingAtOrigin(g, kind)
    ghost.add(g)
    this.paletteDrag = { kind, ghost }
    this.follower.setVisible(false)
  }

  private drawToppingAtOrigin(g: Phaser.GameObjects.Graphics, kind: ToppingKind) {
    switch (kind) {
      case 'candle':
        this.drawCandleGraphic(g, 0, 0, 0xff5577)
        break
      case 'starfruit':
        this.drawStarfruit(g, 0, 0, 22)
        break
      case 'strawberry':
        this.drawStrawberry(g, 0, 0, 20)
        break
      case 'blueberry':
        this.drawBlueberry(g, 0, 0, 12)
        break
      case 'chocolate':
        this.drawChocolateChunk(g, 0, 0, 18)
        break
    }
  }

  // ---------- Pointer handling ----------

  private onPointerDown(pointer: Phaser.Input.Pointer) {
    if (this.paletteDrag) return
    if (this.draggingObject) return
    if (!this.paintTool) return
    if (!this.isOnCake(pointer.x, pointer.y)) return
    this.painting = true
    this.lastPaint = { x: pointer.x, y: pointer.y }
    this.stampPaint(pointer.x, pointer.y)
  }

  private onPointerMove(pointer: Phaser.Input.Pointer) {
    // Follower tracks pointer always (hidden during palette drag)
    if (this.paletteDrag) {
      this.paletteDrag.ghost.setPosition(pointer.x, pointer.y)
      this.follower.setVisible(false)
      return
    }
    if (!this.draggingObject && this.paintTool) {
      this.follower.setVisible(true)
      this.follower.setPosition(pointer.x, pointer.y)
    } else {
      this.follower.setVisible(false)
    }
    if (!this.painting || this.draggingObject) return
    if (!this.isOnCake(pointer.x, pointer.y)) {
      this.lastPaint = undefined
      return
    }
    const from = this.lastPaint ?? { x: pointer.x, y: pointer.y }
    const dx = pointer.x - from.x
    const dy = pointer.y - from.y
    const dist = Math.hypot(dx, dy)
    const step = this.paintTool === 'cream' ? 6 : 3
    const steps = Math.max(1, Math.ceil(dist / step))
    for (let i = 1; i <= steps; i++) {
      const t = i / steps
      this.stampPaint(from.x + dx * t, from.y + dy * t)
    }
    this.lastPaint = { x: pointer.x, y: pointer.y }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer) {
    if (this.paletteDrag) {
      const drop = this.paletteDrag
      const onCake = this.isOnCake(pointer.x, pointer.y)
      if (onCake) {
        this.placeItem(drop.kind, pointer.x, pointer.y)
      }
      drop.ghost.destroy()
      this.paletteDrag = null
      return
    }
    this.painting = false
    this.lastPaint = undefined
  }

  private isOnCake(x: number, y: number) {
    const nx = (x - CAKE_CX) / CAKE_RX
    const topDy = (y - CAKE_CY) / CAKE_RY
    if (nx * nx + topDy * topDy <= 1) return true
    const botDy = (y - (CAKE_CY + CAKE_SIDE_H)) / CAKE_RY
    if (nx * nx + botDy * botDy <= 1 && y >= CAKE_CY + CAKE_SIDE_H) return true
    if (y >= CAKE_CY && y <= CAKE_CY + CAKE_SIDE_H && Math.abs(x - CAKE_CX) <= CAKE_RX) return true
    return false
  }

  // ---------- Painting ----------

  private stampPaint(x: number, y: number) {
    if (this.paintTool === 'cream') this.paintRT.draw(this.creamStamp, x, y)
    else if (this.paintTool === 'icing-pink') this.paintRT.draw(this.icingStampPink, x, y)
    else if (this.paintTool === 'icing-choco') this.paintRT.draw(this.icingStampChoco, x, y)
  }

  // ---------- Placement ----------

  private placeItem(kind: ToppingKind, x: number, y: number) {
    if (kind === 'candle') {
      const c = this.makeCandle(x, y)
      this.candleLayer.add(c)
      this.candles.push(c)
      if (this.candlesLit) this.addFlame(c)
      this.popIn(c)
    } else {
      const c = this.makeTopping(kind, x, y)
      this.toppingLayer.add(c)
      this.toppings.push(c)
      this.popIn(c)
    }
  }

  private popIn(c: Phaser.GameObjects.Container) {
    c.setScale(0.4)
    this.tweens.add({
      targets: c,
      scale: 1,
      duration: 180,
      ease: 'Back.out',
    })
  }

  private makeTopping(kind: ToppingKind, x: number, y: number) {
    const c = this.add.container(x, y)
    const g = this.add.graphics()
    this.drawToppingAtOrigin(g, kind)
    c.add(g)
    const hitRadius = kind === 'blueberry' ? 14 : 24
    c.setSize(hitRadius * 2, hitRadius * 2)
    c.setInteractive(new Phaser.Geom.Circle(0, 0, hitRadius), Phaser.Geom.Circle.Contains)
    this.input.setDraggable(c)
    return c
  }

  private makeCandle(x: number, y: number) {
    const c = this.add.container(x, y)
    const g = this.add.graphics()
    const palette = [0xff5577, 0x57b7ff, 0xffd84a, 0x9d6fff, 0x6ed08a]
    const color = palette[this.candles.length % palette.length]
    this.drawCandleGraphic(g, 0, 0, color)
    c.add(g)
    c.setSize(24, 44)
    c.setInteractive(new Phaser.Geom.Rectangle(-12, -34, 24, 44), Phaser.Geom.Rectangle.Contains)
    this.input.setDraggable(c)
    ;(c as any).__flame = null
    return c
  }

  private drawCandleGraphic(g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number) {
    g.fillStyle(color, 1)
    g.fillRoundedRect(cx - 5, cy - 28, 10, 32, 2)
    g.fillStyle(0xffffff, 0.55)
    for (let i = -22; i < 4; i += 8) g.fillRect(cx - 5, cy + i, 10, 2)
    g.fillStyle(0x3a2a1a, 1)
    g.fillRect(cx - 1, cy - 34, 2, 8)
  }

  // ---------- Decoration drawings ----------

  private drawStarfruit(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number) {
    const pts: { x: number; y: number }[] = []
    const inner = r * 0.42
    for (let i = 0; i < 10; i++) {
      const a = (i * Math.PI) / 5 - Math.PI / 2
      const rad = i % 2 === 0 ? r : inner
      pts.push({ x: cx + Math.cos(a) * rad, y: cy + Math.sin(a) * rad })
    }
    g.fillStyle(0xffe066, 1)
    g.beginPath()
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    g.closePath()
    g.fillPath()
    g.lineStyle(2, 0xb88f2a, 1)
    g.beginPath()
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    g.closePath()
    g.strokePath()
    g.fillStyle(0xfff6a8, 0.8)
    g.fillCircle(cx - r * 0.1, cy - r * 0.1, r * 0.2)
  }

  private drawStrawberry(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number) {
    g.fillStyle(0xe63946, 1)
    g.fillCircle(cx, cy, r * 0.85)
    g.fillTriangle(cx - r * 0.8, cy, cx + r * 0.8, cy, cx, cy + r * 1.25)
    g.fillStyle(0xfff2b0, 1)
    const seeds = [
      [-0.4, -0.3],
      [0.4, -0.2],
      [0, 0.1],
      [-0.35, 0.35],
      [0.35, 0.35],
      [0, -0.55],
    ]
    seeds.forEach(([sx, sy]) => g.fillCircle(cx + sx * r, cy + sy * r, 1.3))
    g.fillStyle(0x3aa055, 1)
    g.fillTriangle(cx - r * 0.7, cy - r * 0.6, cx + r * 0.7, cy - r * 0.6, cx, cy - r * 1.2)
    g.fillTriangle(cx - r * 0.5, cy - r * 0.5, cx + r * 0.5, cy - r * 0.5, cx, cy - r * 0.95)
    g.fillStyle(0xffffff, 0.35)
    g.fillCircle(cx - r * 0.3, cy - r * 0.2, r * 0.2)
  }

  private drawBlueberry(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number) {
    g.fillStyle(0x1e3a8a, 1)
    g.fillCircle(cx, cy, r)
    g.fillStyle(0x3b5bdb, 1)
    g.fillCircle(cx - r * 0.2, cy - r * 0.2, r * 0.75)
    g.fillStyle(0x1e3a8a, 1)
    g.fillCircle(cx, cy - r * 0.15, r * 0.2)
    g.fillStyle(0xffffff, 0.45)
    g.fillCircle(cx - r * 0.4, cy - r * 0.4, r * 0.22)
  }

  private drawChocolateChunk(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number) {
    const w = r * 1.6
    const h = r * 1.2
    g.fillStyle(0x4a2a14, 1)
    g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 3)
    g.fillStyle(0x6b3a1d, 1)
    g.fillRoundedRect(cx - w / 2 + 2, cy - h / 2 + 2, w - 4, h - 4, 2)
    g.fillStyle(0x2a1608, 1)
    g.fillRect(cx - w / 2 + 4, cy - 1, w - 8, 2)
    g.fillRect(cx - 1, cy - h / 2 + 3, 2, h - 6)
    g.fillStyle(0xffffff, 0.2)
    g.fillRect(cx - w / 2 + 3, cy - h / 2 + 3, w - 6, 2)
  }

  // ---------- Action buttons ----------

  private createActionButtons() {
    this.makeActionButton(this.scale.width - 90, 130, '点蜡烛 🔥', 0xff7a3a, () =>
      this.lightCandles(),
    )
    this.makeActionButton(this.scale.width - 90, 188, '吹熄', 0x6a8ec6, () =>
      this.extinguishCandles(),
    )
    this.makeActionButton(this.scale.width - 90, 246, '清空', 0x999999, () => this.resetCake())
  }

  private makeActionButton(x: number, y: number, label: string, color: number, cb: () => void) {
    const c = this.add.container(x, y)
    const bg = this.add.graphics()
    const w = 140
    const h = 44
    bg.fillStyle(color, 1)
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 14)
    bg.lineStyle(2, 0x00000022, 1)
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 14)
    const text = this.add
      .text(0, 0, label, {
        fontFamily: FONT_STACK,
        fontSize: '16px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
    c.add([bg, text])
    c.setSize(w, h)
    c.setInteractive({ useHandCursor: true })
    c.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.()
      cb()
    })
    c.on('pointerover', () => bg.setAlpha(0.85))
    c.on('pointerout', () => bg.setAlpha(1))
    return c
  }

  // ---------- Candles ----------

  private lightCandles() {
    if (this.candles.length === 0) {
      this.showStatus('先插几根蜡烛吧 🎂')
      return
    }
    this.candlesLit = true
    this.candles.forEach((c) => {
      if (!(c as any).__flame) this.addFlame(c)
    })
    this.showStatus('许个愿吧 ✨')
  }

  private extinguishCandles() {
    this.candlesLit = false
    this.candles.forEach((c) => {
      const f = (c as any).__flame as Phaser.GameObjects.Container | null
      if (f) {
        f.destroy()
        ;(c as any).__flame = null
      }
    })
  }

  private addFlame(candle: Phaser.GameObjects.Container) {
    const flame = this.add.container(0, 0)
    const g = this.add.graphics()
    g.fillStyle(0xffcc33, 1)
    g.fillEllipse(0, -42, 8, 14)
    g.fillStyle(0xff7a1a, 1)
    g.fillEllipse(0, -40, 5, 10)
    g.fillStyle(0xffffaa, 0.9)
    g.fillCircle(0, -42, 2)
    const glow = this.add.graphics()
    glow.fillStyle(0xffaa33, 0.25)
    glow.fillCircle(0, -42, 16)
    flame.add([glow, g])
    candle.add(flame)
    ;(candle as any).__flame = flame

    this.tweens.add({
      targets: g,
      scaleY: { from: 0.9, to: 1.15 },
      scaleX: { from: 1, to: 0.9 },
      duration: 180,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.15, to: 0.35 },
      duration: 260,
      yoyo: true,
      repeat: -1,
    })
  }

  // ---------- Reset ----------

  private resetCake() {
    this.paintRT.clear()
    this.candles.forEach((c) => c.destroy())
    this.toppings.forEach((c) => c.destroy())
    this.candles = []
    this.toppings = []
    this.candlesLit = false
    this.showStatus('蛋糕已重置')
  }

  // ---------- Status ----------

  private showStatus(msg: string) {
    this.statusText.setText(msg)
    this.statusTimer?.remove()
    this.statusTimer = this.time.delayedCall(2400, () => {
      this.statusText.setText(
        '顶部选涂抹工具→在蛋糕上点击或拖动；左侧辅料→按住拖到蛋糕上放下',
      )
    })
  }
}
